interface TransformImageOptions {
  targetWidth?: number;
  targetHeight?: number;
  quality?: number;
  format?: 'jpeg' | 'png';
}

/**
 * Transforma una imagen para cumplir con las especificaciones de la app móvil:
 * - Alto fijo de 200px
 * - Ancho dinámico según proporción original (mínimo 1200px, máximo 2400px)
 * - Formato JPG o PNG
 * - Estrategia híbrida: recorte mínimo + dimensiones inteligentes
 * - Usa gradientes suaves en bordes para mejor compatibilidad con resizeMode="cover"
 */
export const transformImageForMobile = async (
  file: File,
  options: TransformImageOptions = {},
): Promise<File> => {
  const { targetHeight = 200, quality = 0.9, format = 'jpeg' } = options;

  try {
    // Leer la imagen original para obtener sus dimensiones
    const img = await loadImage(file);
    const originalWidth = img.width;
    const originalHeight = img.height;
    const originalRatio = originalWidth / originalHeight;

    // Calcular ancho objetivo basado en la proporción original
    // Mínimo 1200px (6:1), máximo 2400px (12:1)
    const minWidth = 1200;
    const maxWidth = 2400;
    const idealRatio = 6; // Proporción ideal 6:1

    let targetWidth: number;
    let strategy: 'crop' | 'scale' | 'hybrid';

    // Determinar estrategia según la proporción de la imagen
    if (
      originalRatio >= idealRatio * 0.8 &&
      originalRatio <= idealRatio * 1.2
    ) {
      // Imagen cercana a 6:1 (±20%): recorte mínimo
      strategy = 'crop';
      targetWidth = minWidth;
    } else if (originalRatio < idealRatio * 0.8) {
      // Imagen vertical/cuadrada: usar ancho mayor para no perder contenido
      strategy = 'scale';
      // Calcular ancho que permita ver la imagen completa
      targetWidth = Math.min(Math.ceil(targetHeight * originalRatio), maxWidth);
      // Asegurar mínimo de 1200px
      targetWidth = Math.max(targetWidth, minWidth);
    } else {
      // Imagen muy ancha: mantener en límite razonable
      strategy = 'hybrid';
      targetWidth = minWidth;
    }

    const targetRatio = targetWidth / targetHeight;

    // Crear canvas con las dimensiones calculadas
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('No se pudo crear el contexto del canvas');
    }

    // Aplicar estrategia según el tipo de imagen
    if (strategy === 'crop' || strategy === 'hybrid') {
      // ESTRATEGIA DE RECORTE: Para imágenes cercanas a la proporción objetivo
      let cropWidth = originalWidth;
      let cropHeight = originalHeight;
      let cropX = 0;
      let cropY = 0;

      if (originalRatio > targetRatio) {
        // Imagen más ancha: recortar los lados
        cropWidth = originalHeight * targetRatio;
        cropX = (originalWidth - cropWidth) / 2;
      } else {
        // Imagen más alta: recortar arriba/abajo
        cropHeight = originalWidth / targetRatio;
        cropY = (originalHeight - cropHeight) / 2;
      }

      // Dibujar imagen recortada escalada al tamaño objetivo
      ctx.drawImage(
        img,
        cropX,
        cropY,
        cropWidth,
        cropHeight,
        0,
        0,
        targetWidth,
        targetHeight,
      );
    } else {
      // ESTRATEGIA DE ESCALADO CON GRADIENTES: Para imágenes muy diferentes
      // Crear fondo con gradiente suave
      if (format === 'jpeg') {
        // Fondo base blanco
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, targetWidth, targetHeight);
      }

      // Calcular dimensiones escaladas para que quepa completa
      let scaledWidth: number;
      let scaledHeight: number;
      let offsetX = 0;
      let offsetY = 0;

      if (originalRatio > targetRatio) {
        scaledWidth = targetWidth;
        scaledHeight = targetWidth / originalRatio;
        offsetY = (targetHeight - scaledHeight) / 2;
      } else {
        scaledHeight = targetHeight;
        scaledWidth = targetHeight * originalRatio;
        offsetX = (targetWidth - scaledWidth) / 2;
      }

      // Dibujar imagen escalada y centrada
      ctx.drawImage(
        img,
        0,
        0,
        originalWidth,
        originalHeight,
        offsetX,
        offsetY,
        scaledWidth,
        scaledHeight,
      );

      // Aplicar gradientes suaves en los bordes para suavizar transición
      if (offsetX > 0) {
        // Gradientes laterales
        const gradientWidth = Math.min(offsetX * 0.8, 50);

        // Gradiente izquierdo
        const gradLeft = ctx.createLinearGradient(0, 0, gradientWidth, 0);
        gradLeft.addColorStop(
          0,
          format === 'jpeg' ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.3)',
        );
        gradLeft.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = gradLeft;
        ctx.fillRect(0, 0, gradientWidth, targetHeight);

        // Gradiente derecho
        const gradRight = ctx.createLinearGradient(
          targetWidth - gradientWidth,
          0,
          targetWidth,
          0,
        );
        gradRight.addColorStop(0, 'rgba(255,255,255,0)');
        gradRight.addColorStop(
          1,
          format === 'jpeg' ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.3)',
        );
        ctx.fillStyle = gradRight;
        ctx.fillRect(
          targetWidth - gradientWidth,
          0,
          gradientWidth,
          targetHeight,
        );
      }

      if (offsetY > 0) {
        // Gradientes superior e inferior
        const gradientHeight = Math.min(offsetY * 0.8, 30);

        // Gradiente superior
        const gradTop = ctx.createLinearGradient(0, 0, 0, gradientHeight);
        gradTop.addColorStop(
          0,
          format === 'jpeg' ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.3)',
        );
        gradTop.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = gradTop;
        ctx.fillRect(0, 0, targetWidth, gradientHeight);

        // Gradiente inferior
        const gradBottom = ctx.createLinearGradient(
          0,
          targetHeight - gradientHeight,
          0,
          targetHeight,
        );
        gradBottom.addColorStop(0, 'rgba(255,255,255,0)');
        gradBottom.addColorStop(
          1,
          format === 'jpeg' ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.3)',
        );
        ctx.fillStyle = gradBottom;
        ctx.fillRect(
          0,
          targetHeight - gradientHeight,
          targetWidth,
          gradientHeight,
        );
      }
    }

    // Convertir el canvas a un blob
    const processedBlob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Error al crear el blob de la imagen procesada'));
          }
        },
        format === 'jpeg' ? 'image/jpeg' : 'image/png',
        quality,
      );
    });

    // Convertir el blob a File
    const finalFile = new File(
      [processedBlob],
      file.name.replace(/\.\w+$/, `.${format === 'jpeg' ? 'jpg' : 'png'}`),
      {
        type: format === 'jpeg' ? 'image/jpeg' : 'image/png',
      },
    );

    console.log(`Imagen transformada usando estrategia "${strategy}":`, {
      original: `${originalWidth}x${originalHeight}`,
      resultado: `${targetWidth}x${targetHeight}`,
      proporcionOriginal: originalRatio.toFixed(2),
      proporcionFinal: (targetWidth / targetHeight).toFixed(2),
    });

    return finalFile;
  } catch (error) {
    console.error('Error al transformar la imagen:', error);
    throw new Error(
      'No se pudo procesar la imagen. Por favor, intenta con otra imagen.',
    );
  }
};

/**
 * Carga una imagen y devuelve un objeto HTMLImageElement
 */
const loadImage = (file: File): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Error al cargar la imagen'));
    };

    img.src = url;
  });
};

/**
 * Valida que el archivo sea una imagen válida
 */
export const validateImageFile = (file: File): boolean => {
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
  return validTypes.includes(file.type);
};

/**
 * Obtiene información sobre las dimensiones de una imagen
 */
export const getImageDimensions = async (
  file: File,
): Promise<{ width: number; height: number; ratio: number }> => {
  const img = await loadImage(file);
  return {
    width: img.width,
    height: img.height,
    ratio: img.width / img.height,
  };
};
